import {Button, Stack, Typography} from "@mui/material";
import {Edit, Flag} from "@mui/icons-material";
import {useTranslation} from "react-i18next";
import {useEffect, useMemo, useState} from "react";
import {
    getFeatureFlags,
    getFeatureFlagStrategies,
    postFeatureFlag,
    postFeatureFlagStrategy,
} from "../../services/featureFlagService";
import {FeatureFlag} from "../../types/domain/featureFlag";
import {FeatureFlagStrategy} from "../../types/domain/featureFlagStrategy";
import AddIcon from "@mui/icons-material/AddOutlined";
import {Dialog} from "../base/Dialog";
import AddFeatureFlag from "./AdminFeatureFlags/AddFeatureFlag";
import {FeatureFlagStrategiesTable} from "./AdminFeatureFlags/FeatureFlagStrategiesTable";
import FeatureFlagsList from "./AdminFeatureFlags/FeatureFlagsList";
import AddFeatureFlagStrategy from "./AdminFeatureFlags/AddFeatureFlagStrategy";

export const AdminFeatureFlagsView: React.FC = () => {
    const {t} = useTranslation();
    const [featureFlags, setFeatureFlags] = useState<FeatureFlag[]>([]);
    const [selectedTab, setSelectedTab] = useState<string>("");
    const [featureFlagStrategies, setFeatureFlagStrategies] = useState<FeatureFlagStrategy[]>([]);
    const [updateFeatureFlagStrategyDialogVisible, setUpdateFeatureFlagStrategyDialogVisible] = useState(false);
    const [addFeatureFlagDialogVisible, setAddFeatureFlagDialogVisible] = useState(false);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [newFeatureFlagStrategy, setNewFeatureFlagStrategy] = useState<FeatureFlagStrategy | null>(null);

    useEffect(() => {
        void getFeatureFlagsData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (featureFlags.length > 0 && !selectedTab) {
            setSelectedTab(featureFlags[0].name);
        }
    }, [featureFlags, selectedTab]);

    async function getFeatureFlagsData() {
        const _featureFlags = await getFeatureFlags();
        setFeatureFlags(_featureFlags);
        setSelectedTab(featureFlags[0]?.name);
        featureFlagSelect(_featureFlags[0]);
    }

    async function getFeatureFlagStrategiesData(featureFlag: FeatureFlag) {
        const strategies = await getFeatureFlagStrategies(featureFlag.id);
        setFeatureFlagStrategies(strategies);
    }

    function featureFlagSelect(featureFlag: FeatureFlag) {
        setSelectedTab(featureFlag.name);
        void getFeatureFlagStrategiesData(featureFlag);
    }

    const activeFeatureFlag = useMemo(() => {
        return featureFlags?.find((featureFlag) => featureFlag.name === selectedTab);
    }, [featureFlags, selectedTab]);

    async function processAddFeatureFlag() {
        await postFeatureFlag({
            name: name,
            description: description,
        });
        setAddFeatureFlagDialogVisible(false);
        setName("");
        setDescription("");
        void getFeatureFlagsData();
    }

    async function processAddFeatureFlagStrategy() {
        if (!activeFeatureFlag) return;

        await postFeatureFlagStrategy({
            featureFlagId: activeFeatureFlag.id,
            data: {
                companyIds:
                    newFeatureFlagStrategy?.selector && ["ALL", "NONE"].includes(newFeatureFlagStrategy.selector)
                        ? []
                        : newFeatureFlagStrategy?.companyIds,
                config: newFeatureFlagStrategy?.config,
                selector: newFeatureFlagStrategy?.selector,
                strategy: newFeatureFlagStrategy?.strategy,
            },
        });
        setNewFeatureFlagStrategy(null);
        setUpdateFeatureFlagStrategyDialogVisible(false);
        void getFeatureFlagStrategiesData(activeFeatureFlag);
    }

    return (
        <Stack direction="row">
            <FeatureFlagsList
                featureFlags={featureFlags}
                t={t}
                selectedTab={selectedTab}
                setAddFeatureFlagDialogVisible={setAddFeatureFlagDialogVisible}
                featureFlagSelect={featureFlagSelect}
            ></FeatureFlagsList>
            <Stack
                sx={{
                    padding: "1.125rem",
                    backgroundColor: "background.default",
                    display: "flex",
                    flexGrow: 1,
                    marginTop: "75px",
                    marginRight: "1.125rem",
                }}
            >
                <Typography
                    fontSize={22}
                    display={"flex"}
                    alignItems="center"
                    sx={{textTransform: "uppercase"}}
                    color="primary"
                >
                    <Flag sx={{marginRight: "12px"}} />

                    {activeFeatureFlag?.name}
                    <Button
                        sx={{whiteSpace: "nowrap", marginLeft: "auto"}}
                        startIcon={<AddIcon />}
                        onClick={() => setUpdateFeatureFlagStrategyDialogVisible(true)}
                    >
                        {featureFlagStrategies?.length
                            ? t("feature_flags_page.update_strategy")
                            : t("feature_flags_page.create_strategy")}
                    </Button>
                </Typography>
                <Typography
                    fontStyle={"italic"}
                    fontSize={14}
                    color="#000000A0"
                    marginTop={"1rem"}
                    marginBottom={"1rem"}
                >
                    {activeFeatureFlag?.description}
                </Typography>
                <Stack>
                    <FeatureFlagStrategiesTable
                        featureFlagStrategies={featureFlagStrategies}
                        setUpdateFeatureFlagStrategyDialogVisible={setUpdateFeatureFlagStrategyDialogVisible}
                    ></FeatureFlagStrategiesTable>
                </Stack>
            </Stack>

            <Dialog
                title={t("feature_flags_page.add_feature_flag")}
                titleIcon={<Edit />}
                open={addFeatureFlagDialogVisible}
                onClose={() => {
                    setAddFeatureFlagDialogVisible(false);
                }}
                enableSubmit={true}
                onSubmit={() => void processAddFeatureFlag()}
            >
                <AddFeatureFlag setName={setName} setDescription={setDescription}></AddFeatureFlag>
            </Dialog>

            <Dialog
                title={t("feature_flags_page.update_feature_flag_strategy")}
                titleIcon={<Edit />}
                open={updateFeatureFlagStrategyDialogVisible}
                onClose={() => {
                    setUpdateFeatureFlagStrategyDialogVisible(false);
                }}
                enableSubmit={true}
                onSubmit={() => void processAddFeatureFlagStrategy()}
                width={"1000px"}
            >
                <AddFeatureFlagStrategy
                    activeStrategy={featureFlagStrategies[0]}
                    featureFlag={activeFeatureFlag}
                    setNewFeatureFlagStrategy={setNewFeatureFlagStrategy}
                    newFeatureFlagStrategy={newFeatureFlagStrategy}
                ></AddFeatureFlagStrategy>
            </Dialog>
        </Stack>
    );
};
